import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Typography,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { RootState, useAppDispatch } from "../../store";
import { Agent, EHR } from "../../types";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { InputStyles } from "../../styles/GeneralStyles";
import { Colors } from "../../Colors";
import FunctionalModulesLayout from "./FunctionalModulesLayout";
import { PrimaryButton } from "../../subcomponents/CustomButton";

export default function Reminders() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = agents && agents.length > 0 ? agents[0] : null;

  const [usingWebptReminders, setUsingWebptReminders] =
    useState<boolean>(false);
  const [reminders, setReminders] = useState<boolean>(false);
  const [reminderTime, setReminderTime] = useState<string | undefined>(
    undefined
  );
  const [reminderDaysBeforeAppointment, setReminderDaysBeforeAppointment] =
    useState<number | undefined>(undefined);
  const [enableReminderConfirmation, setEnableReminderConfirmation] =
    useState<boolean>(false);
  const [
    pushReminderConfirmationToWebpt,
    setPushReminderConfirmationToWebpt,
  ] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (agent) {
      setUsingWebptReminders(agent.usingWebptReminders || false);
      setReminders(agent.reminders || false);
      setReminderTime(agent.reminderTime || undefined);
      setReminderDaysBeforeAppointment(
        agent.reminderDaysBeforeAppointment || undefined
      );
      setEnableReminderConfirmation(agent.enableReminderConfirmation || false);
      setPushReminderConfirmationToWebpt(
        agent.pushReminderConfirmationToWebpt || false
      );
    } else {
      setUsingWebptReminders(false);
      setReminders(false);
      setReminderTime(undefined);
      setReminderDaysBeforeAppointment(undefined);
      setEnableReminderConfirmation(false);
      setPushReminderConfirmationToWebpt(false);
    }
  }, [agent]);

  const handleSave = useCallback(async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const agentProps: Partial<Agent> = {
        agentId: agent.agentId,
        usingWebptReminders: usingWebptReminders,
        reminders: reminders,
        reminderTime: reminderTime,
        reminderDaysBeforeAppointment: reminderDaysBeforeAppointment,
        enableReminderConfirmation: enableReminderConfirmation,
        pushReminderConfirmationToWebpt: pushReminderConfirmationToWebpt
      };
      await dispatch(
        updateAgentThunk({ token: user.token, agent: agentProps })
      ).unwrap();
      showMessage("Reminder settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update reminder settings:", error);
      showMessage("Failed to update reminder settings.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [
    agent,
    user?.token,
    usingWebptReminders,
    reminders,
    reminderTime,
    reminderDaysBeforeAppointment,
    enableReminderConfirmation,
    pushReminderConfirmationToWebpt,
    dispatch,
    showMessage
  ]);

  if (!agent) {
    return (
      <FunctionalModulesLayout>
        <CircularProgress size={30} />
      </FunctionalModulesLayout>
    );
  }

  const showReminderSettings = agent.ehr === EHR.webpt;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <FunctionalModulesLayout>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Reminders
        </Typography>
        <Box display="flex" alignItems="center" ml="-10px">
          <Checkbox
            checked={usingWebptReminders}
            onChange={(e) => setUsingWebptReminders(e.target.checked)}
            sx={{ color: Colors.grey2 }}
          />
          <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
            Use WebPT reminders
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}
        >
          Appointments booked through Penciled will use WebPT reminders.
        </Typography>
        {showReminderSettings && (
          <Box mt={2}>
            <Box display="flex" alignItems="center" ml="-10px">
              <Checkbox
                checked={reminders}
                onChange={(e) => setReminders(e.target.checked)}
                sx={{ color: Colors.grey2 }}
              />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
                Send appointment reminders to patients
              </Typography>
            </Box>

            {reminders && (
              <>
                <Box mt={2} mb="20px" sx={{ maxWidth: "250px", pl: "30px" }}>
                  <TimePicker
                    label="Reminder time"
                    value={reminderTime ? DateTime.fromISO(reminderTime) : null}
                    onChange={(value) => {
                      const isoTime = value ? value.toISO() : null;
                      setReminderTime(isoTime === null ? undefined : isoTime);
                    }}
                    sx={{ ...InputStyles }}
                  />
                </Box>
                {/* Reminder x days before appointment */}
                <Box mt={2} mb="20px" sx={{ maxWidth: "250px", pl: "30px" }}>
                  <TextField
                    type="number"
                    label="Days before reminder"
                    value={reminderDaysBeforeAppointment}
                    onChange={(e) =>
                      setReminderDaysBeforeAppointment(
                        Number(e.target.value) || undefined
                      )
                    }
                    sx={{ ...InputStyles }}
                  />
                </Box>
              </>
            )}

            <Box display="flex" alignItems="center" ml="-10px" mt={1}>
              <Checkbox
                checked={enableReminderConfirmation}
                onChange={(e) =>
                  setEnableReminderConfirmation(e.target.checked)
                }
                sx={{ color: Colors.grey2 }}
              />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
                Confirm if patient is coming to the appointment
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}
            >
              If the patient indicates they are not coming, the agent will
              trigger a cancellation/reschedule flow based on the module
              settings.
            </Typography>

            {enableReminderConfirmation && (
              <>
                <Box display="flex" alignItems="center" ml="20px" mt={1}>
                  <Checkbox
                    checked={pushReminderConfirmationToWebpt}
                    onChange={(e) =>
                      setPushReminderConfirmationToWebpt(e.target.checked)
                    }
                    sx={{ color: Colors.grey2 }}
                  />
                  <Typography
                    style={{ fontSize: "0.9rem", color: Colors.white }}
                  >
                    Push confirmation to WebPT as an appointment note
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: Colors.grey1,
                    pl: "60px",
                  }}
                >
                  When the patient confirms they can make their appointment,
                  the agent will prepend a confirmation note to the
                  corresponding WebPT appointment.
                </Typography>
              </>
            )}

            <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PrimaryButton onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save Changes"
                )}
              </PrimaryButton>
            </Box>
          </Box>
        )}
        <Accordion disableGutters sx={{ mt: 6, boxShadow: "none", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" color="text.secondary">
              Debug: Reminders Timestamps
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: 180 }}>
                Last triggered
              </Typography>
              {organization?.remindersLastTriggeredAt ? (
                <Chip
                  size="small"
                  label={DateTime.fromISO(organization.remindersLastTriggeredAt).toRelative()}
                  title={organization.remindersLastTriggeredAt}
                  sx={{ fontFamily: "monospace", fontSize: 11 }}
                />
              ) : (
                <Typography variant="body2" color="text.disabled">
                  never
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </FunctionalModulesLayout>
    </LocalizationProvider>
  );
}
